import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Check, ShoppingCart, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { getPhones, formatEUR, resolveImageUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";
import { motion } from "framer-motion";
import { PaypalBadge } from "@/components/PaypalBadge";
import { PHONES } from "@/constants/testIds";

const BRANDS = ["Tutti", "Samsung", "Oppo", "Motorola"];

export const PhoneShowcase = ({ hideHeading = false }) => {
  const [phones, setPhones] = useState([]);
  const [brand, setBrand] = useState("Tutti");
  const { selectPhone, phone: selected } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    getPhones().then(setPhones).catch(() => {});
  }, []);

  const filtered = brand === "Tutti" ? phones : phones.filter((p) => p.brand === brand);

  const handleSelect = (p) => {
    selectPhone(p);
    toast.success(`${p.brand} ${p.name} selezionato`, { description: "Include 1 anno di pannello cloud. Completa l'ordine." });
    navigate("/checkout");
  };

  return (
    <section id="telefoni" data-testid={PHONES.section} className="py-16 sm:py-24">
      <div className="container-x">
        {!hideHeading && (
          <Reveal className="max-w-2xl">
            <p className="text-sm font-semibold text-accent uppercase tracking-wider">Vetrina Telefoni</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">Telefoni già configurati e pronti all'uso</h2>
            <p className="mt-4 text-muted-foreground text-base leading-relaxed">
              Scegli tra gli ultimi modelli Samsung, Oppo e Motorola, inclusi i pieghevoli Galaxy Z Flip. Ogni telefono
              arriva con il servizio già installato e <strong className="text-foreground">1 anno di abbonamento al pannello cloud incluso</strong>.
            </p>
          </Reveal>
        )}

        <Reveal delay={0.06} className={hideHeading ? "" : "mt-8"}>
          <div className="flex flex-wrap gap-2" data-testid={PHONES.brandFilter}>
            {BRANDS.map((b) => (
              <button
                key={b}
                onClick={() => setBrand(b)}
                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
                  brand === b
                    ? "bg-primary text-primary-foreground border-primary glow"
                    : "bg-card text-muted-foreground border-border hover:text-foreground"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              layout
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
            >
              <Card data-testid={PHONES.card} className="group h-full overflow-hidden rounded-2xl border-border card-lift glow-hover flex flex-col">
                <div className="relative bg-secondary/50">
                  <AspectRatio ratio={4 / 3}>
                    <img src={resolveImageUrl(p.image)} alt={`${p.brand} ${p.name}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </AspectRatio>
                  {p.badge && (
                    <Badge className="absolute left-3 top-3 rounded-lg bg-primary text-primary-foreground glow">{p.badge}</Badge>
                  )}
                  {selected?.id === p.id && (
                    <Badge className="absolute right-3 top-3 rounded-lg bg-emerald-600 text-white">
                      <Check className="h-3 w-3" /> Selezionato
                    </Badge>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-xs font-medium text-accent uppercase tracking-wide">{p.brand}</p>
                  <h3 className="mt-1 text-lg font-semibold">{p.name}</h3>
                  <ul className="mt-3 space-y-1.5 flex-1">
                    {p.specs.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Check className="h-3.5 w-3.5 text-accent mt-0.5 shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                  {p.subscription_included && (
                    <div className="mt-3 flex items-center gap-2 rounded-lg bg-primary/10 border border-primary/20 px-2.5 py-2 text-xs font-medium text-foreground">
                      <ShieldCheck className="h-3.5 w-3.5 text-accent shrink-0" />
                      Include {p.subscription_included} di pannello cloud
                    </div>
                  )}
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Prezzo</p>
                      <p className="font-display text-xl font-semibold">{formatEUR(p.price)}</p>
                    </div>
                    <Button data-testid={PHONES.select} onClick={() => handleSelect(p)} className="rounded-xl glow-hover">
                      <ShoppingCart className="h-4 w-4" /> Acquista
                    </Button>
                  </div>
                  <PaypalBadge testId={`paypal-badge-phone-${p.id}`} className="mt-3" />
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          * I prezzi includono il telefono, il servizio già installato e 1 anno di abbonamento al pannello cloud. Disponibilità e prezzo finale confermati via email.
        </p>
      </div>
    </section>
  );
};
